import waitingRoomDispatcher from '../../action/waitingRoom';
import {
  TINY_API_KEY,
  TINY_INIT_CONFIGURATION,
} from '@/constants/editorConfig';
import { formatOldData } from '@/helpers';
import { romeoServiceSingleton } from '@/helpers/romeoServices';
import CustomButton from '@/new-components/CustomButton';
import CustomModalTitle from '@/new-components/CustomModal/CustomModalTitle';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  withStyles,
} from '@material-ui/core';
import { Editor } from '@tinymce/tinymce-react';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const CustomDialog = withStyles({
  paper: {
    borderRadius: 16,
    width: 800,
  },
})(Dialog);

const CustomDialogContent = withStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    maxHeight: '70vh',
  },
}))(DialogContent);

const CustomDialogActions = withStyles({
  root: {
    margin: 0,
    padding: 16,
    justifyContent: 'flex-end',
  },
})(DialogActions);

const ModalSummary = ({
  open,
  onClose,
  onSubmit,
  selectedItem,
  modalTitle = 'SOAP Summary',
  isView = false,
  summaryData,
  dataSunmmitted,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [summary, setSummary] = useState('');
  const [defaultSummary, setDefaultSummary] = useState('');
  const [consultInfor, setConsultInfor] = useState({});

  const romeoService = romeoServiceSingleton;
  const { callId, isLoadingSummary } = useSelector(
    (state) => state.waitingRoom
  );
  const accessToken = useSelector((state) => state.auth.accessToken);

  const generateSummary = async () => {
    let id = selectedItem?.consultId ? selectedItem?.consultId : callId;
    try {
      await romeoService.generateSummary(accessToken, id);
    } catch (error) {
      console.error('Failed to generate summary:', error);
    }
  };

  const viewSummary = useCallback(() => {
    let viewSummary = `<p><strong>DOCTOR NAME: </strong><span>${consultInfor?.doctorName}</span></p><p><strong>NRIC: </strong><span>${consultInfor?.identityNumber}</span></p><p><strong>LOCATION: </strong><span>${consultInfor?.userAddress}</span></p>`;
    return viewSummary;
  }, [consultInfor]);

  useEffect(() => {
    waitingRoomDispatcher.setState('summaryData', '');
    setSummary('');
    setDefaultSummary('');
    setConsultInfor({});

    if (isView) {
      waitingRoomDispatcher.getSummarySoap(selectedItem?.consultId, (data) => {
        waitingRoomDispatcher.setState('summaryData', data.soapText);
        setConsultInfor(data.consultInfo);
      });
    } else {
      waitingRoomDispatcher.getSummarySoap(callId, (data) => {
        setConsultInfor(data.consultInfo);
      });
      generateSummary();
    }

    return () => {
      waitingRoomDispatcher.setState('summaryData', '');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEmpty(summaryData)) return;
    if (!isEmpty(dataSunmmitted)) {
      setDefaultSummary(dataSunmmitted);
      setSummary(dataSunmmitted);
      return;
    }

    let summary = formatOldData(summaryData);
    const DRUG_ALLERGIES = 'Drug Allergies';
    const SUBJECTIVE = 'SUBJECTIVE';
    const OBJECTIVE = 'OBJECTIVE';
    const ASSESSMENT = 'ASSESSMENT';
    const PLAN = 'PLAN';

    if (summary?.includes(DRUG_ALLERGIES)) {
      summary = summary?.replace(
        DRUG_ALLERGIES,
        '<strong>DRUG ALLERGIES</strong>'
      );
    }
    if (summary?.includes(SUBJECTIVE)) {
      summary = summary?.replace(SUBJECTIVE, '<strong>SUBJECTIVE</strong>');
    }
    if (summary?.includes(OBJECTIVE)) {
      summary = summary?.replace(OBJECTIVE, '<strong>OBJECTIVE</strong>');
    }
    if (summary?.includes(ASSESSMENT)) {
      summary = summary?.replace(ASSESSMENT, '<strong>ASSESSMENT</strong>');
    }
    if (summary?.includes(PLAN)) {
      summary = summary?.replace(PLAN, '<strong>PLAN</strong>');
    }
    setDefaultSummary(summary);
    setSummary(summary);
  }, [dataSunmmitted, summaryData]);

  return (
    <CustomDialog
      maxWidth="md"
      scroll="body"
      disableEnforceFocus
      open={open}
      onClose={onClose}
      disableBackdropClick
    >
      <CustomModalTitle>{modalTitle}</CustomModalTitle>
      <CustomDialogContent dividers>
        {isLoadingSummary ? (
          <Box textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <p>
              <strong>DOCTOR NAME: </strong>
              <span>{consultInfor?.doctorName}</span>
            </p>
            <p>
              <strong>NRIC: </strong>
              <span>{consultInfor.identityNumber}</span>
            </p>
            <p>
              <strong>LOCATION: </strong>
              <span>{consultInfor?.userAddress}</span>
            </p>
            <Editor
              disabled={!isEdit || isView}
              apiKey={TINY_API_KEY}
              init={{
                height: 380,
                ...TINY_INIT_CONFIGURATION,
              }}
              value={summary}
              onEditorChange={(e) => setSummary(e)}
            />
          </>
        )}
      </CustomDialogContent>
      <CustomDialogActions>
        {isView ? (
          <CustomButton
            type="submit"
            variant="outlined"
            color="primary"
            onClick={onClose}
          >
            Close
          </CustomButton>
        ) : (
          <>
            {isEdit ? (
              <CustomButton
                type="submit"
                variant="outlined"
                color="primary"
                onClick={() => {
                  setIsEdit(false);
                  setSummary(defaultSummary);
                }}
              >
                Cancel
              </CustomButton>
            ) : (
              <CustomButton
                type="submit"
                variant="outlined"
                color="primary"
                onClick={() => setIsEdit(true)}
              >
                Edit
              </CustomButton>
            )}
            <CustomButton
              type="submit"
              variant="contained"
              color="primary"
              onClick={() => {
                const consultInfoText = viewSummary();
                onSubmit({
                  soapText: summary,
                  consultInfoText,
                });
              }}
              disabled={isEmpty(summary)}
              style={{ width: isEdit ? 170 : 166 }}
            >
              {isEdit ? 'Update & Submit' : 'Submit'}
            </CustomButton>
          </>
        )}
      </CustomDialogActions>
    </CustomDialog>
  );
};

export default ModalSummary;
